import { useEffect, useRef, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  pulsePhase: number;
}

interface ParticleNetworkProps {
  particleCount?: number;
  particleColor?: string;
  lineColor?: string;
  particleRadius?: number;
  lineWidth?: number;
  maxDistance?: number;
  speed?: number;
  mouseRadius?: number;
  mouseForce?: number;
}

export default function ParticleNetwork({
  particleCount = 50,
  particleColor = '59, 130, 246', // blue-500 RGB
  lineColor = '59, 130, 246',
  particleRadius = 2,
  lineWidth = 1,
  maxDistance = 150,
  speed = 0.3,
  mouseRadius = 200,
  mouseForce = 0.02,
}: ParticleNetworkProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const animationRef = useRef<number>();
  const dimensionsRef = useRef({ width: 0, height: 0 });

  // Initialize particles
  const initParticles = useCallback((width: number, height: number) => {
    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        radius: particleRadius + Math.random() * 1.5,
        opacity: 0.1 + Math.random() * 0.3, // Reduced opacity for subtle effect
        pulsePhase: Math.random() * Math.PI * 2,
      });
    }
    particlesRef.current = particles;
  }, [particleCount, particleRadius, speed]);

  // Animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = dimensionsRef.current;
    const particles = particlesRef.current;
    const mouse = mouseRef.current;
    const time = Date.now() * 0.001;

    // Clear canvas with fade effect for trails
    ctx.fillStyle = 'rgba(15, 23, 42, 0.1)'; // slate-900 with low opacity
    ctx.fillRect(0, 0, width, height);

    // Update and draw particles
    particles.forEach((particle, i) => {
      // Mouse interaction - gentle push/pull
      const dx = mouse.x - particle.x;
      const dy = mouse.y - particle.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < mouseRadius && dist > 0) {
        const force = (mouseRadius - dist) / mouseRadius * mouseForce;
        particle.vx -= (dx / dist) * force;
        particle.vy -= (dy / dist) * force;
      }

      // Apply velocity with damping
      particle.vx *= 0.99;
      particle.vy *= 0.99;

      // Ensure minimum movement
      const currentSpeed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
      if (currentSpeed < speed * 0.5) {
        particle.vx += (Math.random() - 0.5) * speed * 0.1;
        particle.vy += (Math.random() - 0.5) * speed * 0.1;
      }

      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Wrap around edges smoothly
      if (particle.x < -50) particle.x = width + 50;
      if (particle.x > width + 50) particle.x = -50;
      if (particle.y < -50) particle.y = height + 50;
      if (particle.y > height + 50) particle.y = -50;

      // Pulsing opacity
      const pulse = Math.sin(time * 2 + particle.pulsePhase) * 0.2 + 0.8;
      const finalOpacity = particle.opacity * pulse;

      // Draw particle with glow
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);

      // Glow effect
      const gradient = ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, particle.radius * 3
      );
      gradient.addColorStop(0, `rgba(${particleColor}, ${finalOpacity})`);
      gradient.addColorStop(0.5, `rgba(${particleColor}, ${finalOpacity * 0.3})`);
      gradient.addColorStop(1, `rgba(${particleColor}, 0)`);

      ctx.fillStyle = gradient;
      ctx.fill();

      // Core particle
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.radius * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${particleColor}, ${finalOpacity})`;
      ctx.fill();

      // Draw connections
      for (let j = i + 1; j < particles.length; j++) {
        const other = particles[j];
        const ddx = particle.x - other.x;
        const ddy = particle.y - other.y;
        const distance = Math.sqrt(ddx * ddx + ddy * ddy);

        if (distance < maxDistance) {
          const lineOpacity = (1 - distance / maxDistance) * 0.2 * finalOpacity;

          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(other.x, other.y);
          ctx.strokeStyle = `rgba(${lineColor}, ${lineOpacity})`;
          ctx.lineWidth = lineWidth * (1 - distance / maxDistance);
          ctx.stroke();
        }
      }

      // Draw connection to mouse if close enough
      if (dist < maxDistance * 1.5) {
        const lineOpacity = (1 - dist / (maxDistance * 1.5)) * 0.6;
        ctx.beginPath();
        ctx.moveTo(particle.x, particle.y);
        ctx.lineTo(mouse.x, mouse.y);
        ctx.strokeStyle = `rgba(147, 197, 253, ${lineOpacity})`; // blue-300
        ctx.lineWidth = lineWidth * 1.5;
        ctx.stroke();
      }
    });

    // Draw mouse glow
    if (mouse.x > 0 && mouse.y > 0) {
      const mouseGradient = ctx.createRadialGradient(
        mouse.x, mouse.y, 0,
        mouse.x, mouse.y, mouseRadius * 0.5
      );
      mouseGradient.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
      mouseGradient.addColorStop(0.5, 'rgba(59, 130, 246, 0.05)');
      mouseGradient.addColorStop(1, 'rgba(59, 130, 246, 0)');

      ctx.fillStyle = mouseGradient;
      ctx.fillRect(0, 0, width, height);
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [particleColor, lineColor, particleRadius, lineWidth, maxDistance, speed, mouseRadius, mouseForce]);

  // Handle resize
  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    dimensionsRef.current = { width: rect.width, height: rect.height };

    // Reinitialize particles on significant size change
    if (particlesRef.current.length === 0 ||
        Math.abs(particlesRef.current[0]?.x - rect.width / 2) > rect.width) {
      initParticles(rect.width, rect.height);
    }
  }, [initParticles]);

  // Handle mouse move
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    mouseRef.current = { x: -1000, y: -1000 };
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [handleResize, handleMouseMove, handleMouseLeave, animate]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{
        background: 'linear-gradient(to bottom right, #0f172a, #1e1b4b, #0f172a)',
      }}
    />
  );
}
